/**
 * Delivery Configuration page: loads the delivery-steps Firestore document for the
 * current healthcare facility and shows the payload as formatted JSON. Missing
 * documents are treated as an empty state (JSON `null`) instead of an error.
 */
import { useCallback, useEffect, useState } from "react";
import { FirestoreService } from "../../../../../../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../../../../../../api/firebase/references";
import { LoaderMessage } from "../../../../../../components/loaderMessage/loaderMessage";
import { LocalStorageKeys } from "../../../../../../enums/localStorageKeys";
import { getItemFromLocalStorage } from "../../../../../../utils/localStorageHelper";

/**
 * Fetches `api_config/{facilityId}/delivery-steps/v1` and renders it as JSON.
 * When the document is absent, shows JSON `null` and guidance instead of throwing.
 *
 * @returns Delivery steps config viewer
 */
const DeliveryConfig = () => {
  const [payload, setPayload] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resolvedPath, setResolvedPath] = useState<string | null>(null);

  const loadDeliverySteps = useCallback(async () => {
    const organizationId = getItemFromLocalStorage(
      LocalStorageKeys.HEALTHCARE_FACILITY_ID,
    );

    if (!organizationId?.trim()) {
      setError(
        "No healthcare facility id in local storage; cannot load delivery-steps config.",
      );
      setPayload(null);
      setResolvedPath(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const path =
        FirestoreDocumentReference.deliveryStepsConfig(organizationId);
      setResolvedPath(path);
      const data =
        await FirestoreService.getDocumentOrNull<Record<string, unknown>>(path);
      setPayload(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setPayload(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadDeliverySteps();
  }, [loadDeliverySteps]);

  if (isLoading) {
    return (
      <div className="delivery-config flex flex-col gap-4 p-4">
        <LoaderMessage message="Loading delivery steps configuration…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="delivery-config flex flex-col gap-4 p-4">
        <p className="text-body1 text-red-600" role="alert">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="delivery-config flex h-full min-h-0 flex-col gap-4 p-4">
      <p className="text-body1 text-primaryGray-12">
        Raw document from{" "}
        <code className="bg-primaryGray-20 rounded px-1 py-0.5 text-sm">
          api_config / … / delivery-steps / v1
        </code>{" "}
        for the current facility
        {resolvedPath ? (
          <>
            :{" "}
            <code className="bg-primaryGray-20 rounded px-1 py-0.5 text-sm">
              {resolvedPath}
            </code>
          </>
        ) : null}
        .
      </p>
      {payload === null && resolvedPath && !error ? (
        <p className="text-body1 text-amber-800" role="status">
          No Firestore document exists at that path yet. Create or seed{" "}
          <code className="bg-primaryGray-20 rounded px-1 py-0.5 text-sm">
            delivery-steps/v1
          </code>{" "}
          under your facility&apos;s{" "}
          <code className="bg-primaryGray-20 rounded px-1 py-0.5 text-sm">
            api_config
          </code>{" "}
          document, or confirm your logged-in facility id matches where the
          config was written.
        </p>
      ) : null}
      <pre className="bg-primaryGray-20 max-h-[calc(100vh-16rem)] min-h-[12rem] flex-1 overflow-auto rounded border border-primaryGray-16 p-4 font-mono text-sm text-primaryGray-12">
        {JSON.stringify(payload, null, 2)}
      </pre>
    </div>
  );
};

export default DeliveryConfig;
